const triggerSelector = '[data-zendesk-trigger]';

export const zendeskHasLoaded = () => window.zE && typeof window.zE === 'function';
export const zendeskChatHasLoaded = () => window.$zopim && typeof window.$zopim === 'function';

export const getZendeskTriggerElement = () => document.querySelector(triggerSelector);
const zendeskTriggerIsLoading = (trigger: Element) => trigger.classList.contains('loading');
const zendeskIsHidden = (trigger: Element) => trigger.classList.contains('appear-in');
export const zendeskTriggerIsReady = (trigger: Element | null) =>
  trigger && !zendeskTriggerIsLoading(trigger) && zendeskIsHidden(trigger);

const observeElements = ['compare-bar', 'mobile-cta']; // elements to observe for positionZendeskWidget

const getZendeskChatDepartments = (): Promise<Array<{ name: string; status: string; id: number }>> => {
  return new Promise(resolve => {
    const hideLoadingInterval = window.setInterval(() => {
      if (!zendeskHasLoaded() || !zendeskChatHasLoaded()) return;

      const departments = window.$zopim?.livechat.departments.getAllDepartments();

      if (departments.length === 0) return;

      window.clearInterval(hideLoadingInterval);
      window.clearTimeout(clearLoadingInterval);

      resolve(
        departments.map(department => ({
          name: department.name,
          status: department.status,
          id: department.id,
        }))
      );
    }, 200);

    const clearLoadingInterval = setTimeout(() => {
      if (hideLoadingInterval) window.clearInterval(hideLoadingInterval);

      resolve([]);
    }, 5000);
  });
};

export const setZendeskChatDepartment = async (department?: string | undefined) => {
  if (!zendeskChatHasLoaded() || window.$zopim.livechat?.window?.getDisplay()) return;
  if (department) {
    window.$zopim?.livechat.departments.setVisitorDepartment(department);
  } else if (window.Jbhifi.zendeskChatDepartment) {
    window.$zopim?.livechat.departments.setVisitorDepartment(window.Jbhifi.zendeskChatDepartment);
  } else {
    const departments: any = await getZendeskChatDepartments();
    if (departments?.length) window.$zopim?.livechat.departments.setVisitorDepartment(departments[0].name);
  }
  positionZendeskWidget(); // position widget depending on bottom elements after open/close
};

const loadZendeskScript = () => {
  return new Promise((resolve, reject) => {
    if (zendeskHasLoaded()) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${window.Jbhifi.zendeskWidgetKey}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const zendeskIsReady = (trigger?: Element | null) => {
  return new Promise((resolve, reject) => {
    const hideLoadingInterval = window.setInterval(() => {
      if (!zendeskHasLoaded() || !zendeskChatHasLoaded()) return;

      window.clearInterval(hideLoadingInterval);
      window.clearTimeout(clearLoadingInterval);

      if (trigger) trigger?.classList.remove('loading');
      resolve(true);
    }, 200);

    const clearLoadingInterval = setTimeout(() => {
      if (hideLoadingInterval) window.clearInterval(hideLoadingInterval);

      reject('Zendesk failed to be ready');
    }, 15000);
  });
};

export const zendeskTriggerAction = async (trigger: Element | null, department?: string) => {
  if (!window.Jbhifi?.zendeskEnabled || !window.Jbhifi?.zendeskWidgetKey || !zendeskTriggerIsReady(trigger)) return;
  if (!zendeskHasLoaded()) {
    trigger?.classList.add('loading');

    try {
      await loadZendeskScript();

      if (!zendeskHasLoaded()) {
        throw Error('Zendesk not available at init');
      }

      window.zE('webWidget', 'open');

      await zendeskIsReady(trigger);

      await setZendeskChatDepartment(department);

      // Initial show after Zendesk is ready
      window.zE('webWidget', 'show');
      trigger?.classList.remove('appear-in');

      window.zE('webWidget:on', 'open', () => {
        window.zE('webWidget', 'show');
        trigger?.classList.remove('appear-in');
      });

      window.zE('webWidget:on', 'close', () => {
        window.zE('webWidget', 'hide');
        trigger?.classList.add('appear-in');
      });
    } catch (error) {
      trigger?.classList.remove('loading');
      console.error(`Error loading Zendesk Web Widget\n${error}`);
      return;
    }
  } else {
    await setZendeskChatDepartment(department);

    window.zE('webWidget', 'open');
  }
};

export const initZendesk = () => {
  const trigger: Element | null = getZendeskTriggerElement();

  if (!window.Jbhifi?.zendeskEnabled || !window.Jbhifi?.zendeskWidgetKey || !trigger) return;

  setTimeout(() => {
    trigger?.classList.add('appear-in');
  }, 1000);

  trigger.addEventListener('click', () => zendeskTriggerAction(trigger));

  window.Jbhifi.zendeskTriggerAction = zendeskTriggerAction;
};

export const initZendeskDefault = async () => {
  if (window.Jbhifi?.zendeskEnabled || !window.Jbhifi?.zendeskWidgetKey) return;

  try {
    await zendeskIsReady();

    await setZendeskChatDepartment();
    zendeskPosition();
    screen.orientation.addEventListener('change', () => {
      zendeskPosition();
    });
    window.zE('webWidget:on', 'close', async () => {
      await setZendeskChatDepartment();
    });
  } catch (error) {
    console.error(error);
  }
};

const zendeskPosition = () => {
  positionZendeskWidget();
  createObservers();
};

const createObservers = () => {
  // observe elements to position widget depending on bottom elements

  observeElements.forEach(refElement => {
    const trigger: Element | null = document.getElementById(refElement);
    if (trigger) {
      const observer = new MutationObserver(() => {
        positionZendeskWidget();
      });
      observer.observe(trigger, {
        childList: true,
        subtree: true,
      });
      return () => {
        observer.disconnect();
      };
    }
  });
};

export const positionZendeskWidget = () => {
  const helpButton = document.getElementById('launcher');
  if (helpButton) {
    let y = 0;
    observeElements.forEach(refElement => {
      const element = document.getElementById(refElement);
      if (element) {
        const elementOffset = window.innerHeight - element.getBoundingClientRect().y;
        y = y < elementOffset ? elementOffset : y;
      }
    });
    helpButton.style.setProperty('bottom', y + 'px', 'important');
  }
};

export const hideZendeskDepartment = async (departmentId?: number) => {
  if (!departmentId) return;

  const allDepartments = await getZendeskChatDepartments();
  const filteredDepartmentIds = allDepartments
    .filter(department => department.id !== departmentId)
    .map(department => department.id);

  window.$zopim?.livechat?.departments?.filter(...filteredDepartmentIds);
};
